import { Controller } from '@hotwired/stimulus'

// Animates a number from `start` to `end` the first time it scrolls into view.
// Works in either direction, so it counts up (0 → 39) or down (7 → 1, 6 → 0).
//
// If the element is inside a `.reveal` (scroll-reveal fade-in), the count waits
// for that fade to finish before running — otherwise it would animate while the
// element is still invisible and you'd never see it.
//
// Usage:
//   <span data-controller="count-up"
//         data-count-up-start-value="7"
//         data-count-up-end-value="1"
//         data-count-up-duration-value="1600"
//         data-count-up-prefix-value="$"
//         data-count-up-suffix-value="-day">1</span>
export default class extends Controller {
  static values = {
    start: { type: Number, default: 0 },
    end: Number,
    duration: { type: Number, default: 1600 },
    prefix: { type: String, default: '' },
    suffix: { type: String, default: '' },
    threshold: { type: Number, default: 0.12 }
  }

  connect() {
    this.done = false

    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      this.render(this.endValue)
      return
    }

    // Park on the starting number until the element is revealed.
    this.render(this.startValue)

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      threshold: this.thresholdValue,
      rootMargin: '0px 0px -8% 0px'
    })
    this.observer.observe(this.element)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.done) {
        this.done = true
        this.observer.unobserve(this.element)
        // Run immediately so the count animates alongside the fade-in rather
        // than sitting on the starting number first.
        this.animate()
      }
    })
  }

  animate() {
    const from = this.startValue
    const to = this.endValue
    const begin = performance.now()

    const step = (now) => {
      const progress = Math.min((now - begin) / this.durationValue, 1)
      // easeOutCubic for a snappy settle.
      const eased = 1 - Math.pow(1 - progress, 3)
      this.render(Math.round(from + (to - from) * eased))
      if (progress < 1) this.frame = requestAnimationFrame(step)
    }
    this.frame = requestAnimationFrame(step)
  }

  render(value) {
    this.element.textContent = `${this.prefixValue}${value.toLocaleString()}${this.suffixValue}`
  }
}
